import React from "react";
import axios from "axios";
import { Select, Spin, Table, Checkbox, message, Collapse, Tag, Row } from "antd";
import { dataCollectionService, baseUrl } from '../../../api/api.config';

const DataCollectionModule = ({ regionList, currentUserID, isView }) => {
    const [isLoading, setIsLoading] = React.useState(false);
    const [activeCategories, setActiveCategories] = React.useState([]);
    const [userRelatedCategories, setUserRelatedCategories] = React.useState([]);
    const [userRelatedRegions, setUserRelatedRegions] = React.useState([]);

    React.useEffect(() => {
        fetchActiveCategories();
        fetchUserRelatedCategories();
        fetchUserRelatedRegions();
    }, []);

    const fetchActiveCategories = async () => {
        setIsLoading(true);
        try {
            const response = await axios.get(
                `${baseUrl + dataCollectionService}/userPermission/getAllActiveCategories`);
            if (response.status === 200) {
                setActiveCategories(response.data);
            } else {
                console.error("Failed to fetch active categories:", response.statusText);
            }
        } catch (error) {
            console.error("Error fetching active categories:", error);
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    const fetchUserRelatedCategories = async () => {
        setIsLoading(true);
        try {
            const response = await axios.get(`${baseUrl + dataCollectionService}/userPermission/getUserCategories`, {
                headers: {
                    "userId": currentUserID
                }
            });
            if (response.status === 200) {
                setUserRelatedCategories(response.data);
            } else {
                console.error("Failed to fetch user related categories:", response.statusText);
            }
        } catch (error) {
            console.error("Error fetching user related categories:", error);
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    const updateUserRelatedCategories = async (data) => {
        setIsLoading(true);

        try {
            const formData = new FormData();
            formData.append("userCategories", JSON.stringify(data));

            const response = await axios.post(
                `${baseUrl + dataCollectionService}/userPermission/manageUserCategories`,
                formData,
                {
                    headers: {
                        "userId": currentUserID,
                    }
                }
            );
            if (response.status === 200) {
                console.log("User related categories updated successfully:", response.data);
                message.success(<span className='textStyles-small'>User related categories updated successfully</span>);
                fetchUserRelatedCategories();
            } else {
                console.error("Failed to update user related categories:", response.statusText);
            }
        } catch (error) {
            console.error("Error updating user related categories:", error);
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    const fetchUserRelatedRegions = async () => {
        try {
            const response = await axios.post(
                `${baseUrl + dataCollectionService}/userPermission/dataForManageUserSites`, {}, {
                headers: {
                    "userId": currentUserID
                }
            });
            if (response.status === 200) {
                console.log("User related regions fetched successfully:", response.data);
                setUserRelatedRegions(response?.data?.regions || []);
            } else {
                console.error("Failed to fetch user related regions:", response.statusText);
            }
        } catch (error) {
            console.error("Error fetching user related categories:", error);
            throw error;
        }
    };

    const updateUserRelatedRegions = async (data) => {
        console.log("Selected Regions:", data);
        setIsLoading(true);
        try {
            const formData = new FormData();
            formData.append("regionIds", data.join(','));
            const response = await axios.post(
                `${baseUrl + dataCollectionService}/userPermission/manageUserSites`,
                formData,
                {
                    headers: {
                        "userId": currentUserID
                    }
                }
            );
            if (response.status === 200) {
                message.success(<span className='textStyles-small'>User related regions updated successfully</span>);
                fetchUserRelatedRegions();
            } else {
                console.error("Failed to update user related regions:", response.statusText);
            }
        } catch (error) {
            console.error("Error updating user related regions:", error);
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    const handlePermissionToggle = (categoryId, permissionType) => {
        setUserRelatedCategories(prev => {
            const updated = prev.map(item =>
                item.category.id === categoryId
                    ? { ...item, [permissionType]: !item[permissionType] }
                    : item
            );
            updateUserRelatedCategories(updated);
            return updated;
        });
    };

    const columns = [
        {
            title: <span className='textStyles-small' style={{ fontWeight: 500, fontSize: 13 }}>Category</span>,
            dataIndex: 'category.id',
            key: 'category',
            render: (text, record) => activeCategories.find(category => category.id === record.category.id)?.categoryName || text
        },
        {
            title: <span className='textStyles-small' style={{ fontWeight: 500, fontSize: 13 }}>Write Permission</span>,
            dataIndex: 'writePermission',
            key: 'writePermission',
            render: (value, record) => (
                <Checkbox
                    disabled={isView}
                    checked={value}
                    onChange={() => handlePermissionToggle(record.category.id, 'writePermission')}
                />
            ),
        },
        {
            title: <span className='textStyles-small' style={{ fontWeight: 500, fontSize: 13 }}>Read Permission</span>,
            dataIndex: 'readPermission',
            key: 'readPermission',
            render: (value, record) => (
                <Checkbox
                    disabled={isView}
                    checked={value}
                    onChange={() => handlePermissionToggle(record.category.id, 'readPermission')}
                />
            ),
        },
    ];

    return (
        <Spin spinning={isLoading} tip={<span className='textStyles-small'>Fetching Data ....</span>}>
            <Collapse defaultActiveKey={['1']} className='mb-3' style={{ backgroundColor: 'transparent', borderRadius: '10px' }}>
                <Collapse.Panel header={<span className='textStyles-small' style={{ fontWeight: 500, fontSize: 13 }}>User Related Categories</span>}
                    key="1">
                    <>
                        {
                            !isView && (<Select
                                className='borderedSelect'
                                style={{ width: '100%', marginBottom: '20px' }}
                                bordered={false}
                                mode="multiple"
                                placeholder="Select categories"
                                showArrow={true}
                                showAction={['select', 'clear']}
                                value={userRelatedCategories.map(item => item.category.id)}
                                disabled={isView}
                                filterOption={(input, option) =>
                                    option.children.toLowerCase().includes(input.toLowerCase())
                                }
                                onChange={(values) => {
                                    const newCategories = values
                                        .filter(
                                            (id) => !userRelatedCategories.some((item) => item.category.id === id)
                                        )
                                        .map((id) => ({
                                            category: { id },
                                            writePermission: false,
                                            readPermission: false,
                                        }));
                                    if (newCategories.length > 0) {
                                        setUserRelatedCategories((prev) => {
                                            const updated = [...prev, ...newCategories];
                                            updateUserRelatedCategories(updated);
                                            return updated;
                                        });
                                    }
                                }}
                                onDeselect={(value) => {
                                    setUserRelatedCategories(prev => {
                                        const updated = prev.filter(item => item.category.id !== value);
                                        updateUserRelatedCategories(updated);
                                        return updated;
                                    });
                                }}
                                loading={isLoading}
                            >
                                {activeCategories.map(category => (
                                    <Select.Option key={category.id} value={category.id} className='textStyles-small'>
                                        {category.categoryName}
                                    </Select.Option>
                                ))}
                            </Select>)
                        }
                        <Table
                            dataSource={userRelatedCategories}
                            className='table-striped-rows textStyles-small'
                            size="small"
                            columns={columns}
                            rowKey={(record) => record.category.id}
                            pagination={userRelatedCategories.length > 10 ? { pageSize: 10 } : false}
                            loading={isLoading}
                            scroll={{ x: 'max-content' }}
                        />
                    </>
                </Collapse.Panel>
                <Collapse.Panel header={<span className='textStyles-small' style={{ fontWeight: 500, fontSize: 13 }}>User Related Regions</span>} key="2">
                    {
                        isView &&
                        <Row>
                            {
                                userRelatedRegions.length > 0 ?
                                    userRelatedRegions.map(region => (
                                        <Tag
                                            color="blue"
                                            style={{ marginBottom: '5px', borderRadius: '8px', padding: '5px', borderColor: 'transparent' }}
                                            key={region.id}>
                                            <span className='textStyles-small' style={{ fontWeight: 500 }}>{region.name}</span>
                                        </Tag>
                                    )) :
                                    <span className='textStyles-small'>No Regions Assigned</span>
                            }
                        </Row>
                    }
                    {
                        !isView && (
                            <Select
                                className='borderedSelect'
                                showArrow={true}
                                style={{ width: '100%', height: 'auto' }}
                                bordered={false}
                                showSearch
                                mode="multiple"
                                placeholder="Select a Region"
                                filterOption={(input, option) =>
                                    option.children.toLowerCase().includes(input.toLowerCase())
                                }
                                onChange={(value) => {
                                    updateUserRelatedRegions(value);
                                }}
                                disabled={isView}
                                value={userRelatedRegions.map(region => region.id)}
                            >
                                {
                                    regionList.map((data, index) => {
                                        return (
                                            <Select.Option key={data.id} value={data.id} className='textStyles-small'>
                                                {data.name}
                                            </Select.Option>
                                        )
                                    })
                                }
                            </Select>
                        )
                    }
                </Collapse.Panel>
            </Collapse>
        </Spin>
    );
};

export default DataCollectionModule;